import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui';

/** Filter / toolbar card — no overflow clip (content may include popovers). */
export const dashboardFilterCardClass = 'shadow-sm';

/** Table or list card — clips inner scroll and table edges. */
export const dashboardDataCardClass = 'overflow-hidden shadow-sm';

export const dashboardSectionCardHeaderClass =
  'space-y-1 border-b border-border bg-muted/20 px-4 py-4 sm:px-6';

export const dashboardSectionCardTitleClass = 'text-base font-semibold';

export const dashboardFilterCardContentClass = 'px-4 py-5 sm:px-6';

export const dashboardDataCardBodyClass = 'p-0';

/** Column headers inside dashboard data tables (Coach hours, billing report, PaginatedTable dashboard variant). */
export const dashboardTableHeadClass =
  'h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

export const dashboardTableHeaderRowClass = 'border-border hover:bg-transparent';

export const dashboardTableDataRowClass = 'border-border hover:bg-muted/40';

export const dashboardTableCellClass = 'px-4 py-3';

type DashboardSectionCardBaseProps = {
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  headerClassName?: string;
  children: ReactNode;
};

export function DashboardFilterCard({
  title,
  description,
  className,
  headerClassName,
  children,
}: DashboardSectionCardBaseProps) {
  return (
    <Card className={cn(dashboardFilterCardClass, className)}>
      <CardHeader className={cn(dashboardSectionCardHeaderClass, headerClassName)}>
        <CardTitle className={dashboardSectionCardTitleClass}>{title}</CardTitle>
        {description ? <CardDescription className="text-sm">{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className={dashboardFilterCardContentClass}>{children}</CardContent>
    </Card>
  );
}

type DashboardDataCardProps = Omit<DashboardSectionCardBaseProps, 'title'> & {
  /** Omit when the page header (PageHeader / ManagementHeader) already names this list. */
  title?: ReactNode;
  /** Optional action aligned to the right of the card header (e.g. Add button). */
  headerAction?: ReactNode;
  /** Optional row between header and body (bulk actions, result counts, etc.). */
  toolbar?: ReactNode;
  contentClassName?: string;
};

export function DashboardDataCard({
  title,
  description,
  headerAction,
  toolbar,
  className,
  headerClassName,
  contentClassName,
  children,
}: DashboardDataCardProps) {
  const showHeader = title != null || description != null || headerAction != null;

  return (
    <Card className={cn(dashboardDataCardClass, className)}>
      {showHeader ? (
        <CardHeader className={cn(dashboardSectionCardHeaderClass, headerClassName)}>
          {title != null ? (
            <CardTitle className={dashboardSectionCardTitleClass}>{title}</CardTitle>
          ) : null}
          {description ? <CardDescription className="text-sm">{description}</CardDescription> : null}
          {headerAction ? <CardAction>{headerAction}</CardAction> : null}
        </CardHeader>
      ) : null}
      {toolbar ? (
        <div className="border-b border-border bg-muted/30 px-3 py-2.5 sm:px-4">{toolbar}</div>
      ) : null}
      <CardContent className={cn(dashboardDataCardBodyClass, contentClassName)}>{children}</CardContent>
    </Card>
  );
}
